<template>
  <header class="header__container" :class="{ '--bg': pageInfo.headerBgImage }">
    <div
      v-if="pageInfo.headerBgImage"
      class="header__bg animate__animated animate__fadeIn"
      :style="{ '--eco-header-bg-color': pageInfo.headerBgColor }"
    >
      <img :src="pageInfo.headerBgImage" />
    </div>
    <brand-logo
      v-if="showBrandLogo"
      class="header__logo"
      data-testid="container_logo"
    />
    <div class="header__accountControls">
      <page-back
        v-if="!interstitialData"
        back-btn-size="md"
        class="show-mobile header-page-title"
      />
      <language-selector v-if="languageCanBeChanged"
                         v-model="userLanguage"
                         data-testid="container_language_selector"
                         class="header__accountControls__languageSelector hidden-mobile"
                         :hide-arrow-on-mobile="showMenu && isLoggedIn"
                         compact
                         :options="availableLanguages"
      />

      <support-menu v-if="isSupportMenuEnabled" />

      <main-menu
        v-if="showMenu"
        :is-logged-in="isLoggedIn"
        :member="member"
        :lang-options="{ list: availableLanguages, selected: userLanguage }"
        :show-language-option="languageCanBeChanged"
        data-testid="container_menu"
        class="header__accountControls__accountMenu"
        :class="{ hiddenMenuDesktop }"
        @logout="logout"
        @login="() => $router.push({ name: 'Login' })"
        @lang:selected="setLang"
        @goToDeleteAccount="goToDeleteAccount"
      />
    </div>
  </header>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';

// MIXINS
import tracking from '@/mixins/tracking';

// COMPONENTS
import BrandLogo from '@/components/BrandLogo.vue';
import MainMenu from '@/components/layout/MainMenu.vue';
import LanguageSelector from '@/components/layout/LanguageSelector.vue';
import SupportMenu from '@/components/layout/SupportMenu.vue';
import PageBack from '@/components/layout/PageBack.vue';

// VUEX
import {
  authStoreTypes,
  memberStoreTypes,
  i18nStoreTypes,
  enrolmentStoreTypes,
} from '@/store/types';

// CONFIG
import zendesk from '@/plugins/zendesk';

export default {
  name: 'SwordHeader',
  components: {
    BrandLogo,
    MainMenu,
    LanguageSelector,
    SupportMenu,
    PageBack,
  },
  mixins: [tracking],

  inject: {
    pageInfo: {
      default: () => ({}),
    },
  },

  props: {
    showBrandLogo: {
      type: Boolean,
      default: true,
    },
    showMenu: {
      type: Boolean,
      default: true,
    },
    hiddenMenuDesktop: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters({
      isLoggedIn: authStoreTypes.getters.IS_LOGGED_IN,
      member: memberStoreTypes.getters.GET_MEMBER,
      language: i18nStoreTypes.getters.GET_LANGUAGE,
      availableLanguages: i18nStoreTypes.getters.GET_AVAILABLE_LANGUAGES,
      isSupportChatEnabled: enrolmentStoreTypes.getters.GET_SUPPORT_CHAT_ENABLED,
    }),
    ...mapState({
      interstitialData: state => state.application.interstitialData,
    }),
    languageCanBeChanged() {
      return this.availableLanguages?.length > 1;
    },
    userLanguage: {
      get() {
        return this.language;
      },
      async set(language) {
        await this.updateAppLanguage({ userSelectedLanguage: language });
      },
    },
    isCurrentRouteConsiderSupportChatEnabled() {
      return this.$route.meta?.considerSupportChatEnabled || false;
    },
    isSupportMenuEnabled() {
      if (this.isCurrentRouteConsiderSupportChatEnabled) {
        return this.isZendeskEnabled && this.isSupportChatEnabled;
      }

      return this.isZendeskEnabled;
    },
    isZendeskEnabled() {
      return zendesk.options.enabled;
    },
  },

  methods: {
    ...mapActions({
      doLogout: authStoreTypes.actions.LOGOUT,
      updateAppLanguage: i18nStoreTypes.actions.UPDATE_APP_LANGUAGE,
    }),
    logout() {
      this.doLogout();
    },
    goToDeleteAccount() {
      this.$router.push({ name: 'DeleteAccount' });
    },
    setLang(lang) {
      this.userLanguage = lang;
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  &__container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: .5rem 1rem;
    position: relative;

    @media screen and (min-width: $max-mobile-size) {
      padding: .5rem 1.5rem;
    }

    @media screen and (min-width: $tablet-size) {
      padding: 1.25rem 2.5rem;
    }

    ::v-deep {
      .menu-btn {
        min-width: initial;
        padding: 0;
        margin: 0;
        margin-left: .75rem;
        .svg-icon {
          width: 1.5rem;
          height: 1.5rem;
        }

        @media screen and (min-width: $tablet-size) {
          margin-left: .25rem;
        }

        @media screen and (max-width: $tablet-size) {
          &:hover,
          &:focus {
            .svg-icon {
              color: $context-color-1;
            }
          }
        }
      }
    }

    &.--bg {
      margin-bottom: 176px;

      @media screen and (min-width: $tablet-size) {
        margin-bottom: 280px;
      }
    }
  }

  &__logo {
    display: none;
    z-index: 3;

    @media screen and (min-width: $tablet-size) {
      display: block;
    }
  }

  &__accountControls {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    gap: .25rem;
    z-index: 3;

    ::v-deep {
      .header-page-title {
        flex-grow: 1;

        span {
          position: absolute;
          left: 50vw;
          transform: translate(-50%, 0);
          text-align: center;
        }
      }
      .sword-button-wuk {
        text-decoration: none;
      }

    }

    @media screen and (min-width: $tablet-size) {
      gap: 2.5rem;

      .hiddenMenuDesktop {
        display: none !important;
      }
    }
  }

  &__bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    background: center no-repeat url('~@/assets/images/multiple-programs/detail/header-bg.png');
    background-size: cover;
    border-radius: 0 0 1rem 1rem;
    height: 224px;

    @media screen and (min-width: $tablet-size) {
      height: 360px;
    }

    img {
      height: calc(100% - 0.5rem);
      width: auto;
      max-width: 100%;
      object-fit: cover;
      place-self: flex-end;
    }
  }
}
</style>
